import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, RotateCcw, Check, X, BadgeCheck, Loader2, ShieldAlert } from "lucide-react";
import dayjs from "dayjs";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";
import { fetchInvites, reviewInviteThunk } from "../../store/loyaltySlices/membershipInviteSlice";

const PER_PAGE = 25;

const STATUS_PENDING = 0;
const STATUS_APPROVED = 1;
const STATUS_REJECTED = 2;

/* ─────────────────────────────────────────────────────────────────────────────
   API shape lives in ONE place. When the response changes, only edit
   normalizeInviteRow() (row fields) and buildParams() (request body).
   ───────────────────────────────────────────────────────────────────────────── */
export const normalizeInviteRow = (r) => ({
    id: r.id,
    createdAt: r.created_at,
    programName: r.program?.program_name || `Plan #${r.program_id}`,
    customerName: r.user?.name || `Customer #${r.user_id}`,
    customerEmail: r.user?.email || "",
    customerMobile: r.user?.mobile || r.user?.phone || "",
    pan: r.pan_number,
    panVerified: Boolean(r.pan_verified),
    noCriminalRecord: Boolean(r.no_criminal_record),
    consentGiven: Boolean(r.consent_given),
    social: r.social_media_details && typeof r.social_media_details === "object" ? r.social_media_details : {},
    status: Number(r.status),
    reviewedAt: r.reviewed_at,
    reviewerName: r.reviewer?.name || "",
    rejectionReason: r.rejection_reason,
});

const buildParams = (f, page) => ({
    perPage: PER_PAGE,
    page,
    status: f.status === "" ? null : Number(f.status),
    keyword: f.keyword.trim() || null,
});

const defaultFilters = () => ({ status: String(STATUS_PENDING), keyword: "" });

const STATUS_OPTIONS = [
    { value: "", label: "All requests" },
    { value: String(STATUS_PENDING), label: "Pending" },
    { value: String(STATUS_APPROVED), label: "Approved" },
    { value: String(STATUS_REJECTED), label: "Rejected" },
];

/* Static class maps so Tailwind can see every class name */
const STATUS_STYLE = {
    [STATUS_PENDING]: { label: "Pending", badge: "bg-amber-50 text-amber-700 border border-amber-200", edge: "border-l-4 border-amber-400" },
    [STATUS_APPROVED]: { label: "Approved", badge: "bg-green-50 text-green-700 border border-green-200", edge: "border-l-4 border-green-500" },
    [STATUS_REJECTED]: { label: "Rejected", badge: "bg-red-50 text-red-700 border border-red-200", edge: "border-l-4 border-red-500" },
};
const STATUS_FALLBACK = { label: "-", badge: "bg-gray-100 text-gray-700 border border-gray-200", edge: "border-l-4 border-gray-300" };

const SOCIAL_LABELS = { instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", x: "X" };

const inputCls = "w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#f4a406] focus:outline-none";

toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };

/** Approve / reject confirmation. Rejecting needs a reason. */
function ReviewModal({ target, busy, onCancel, onConfirm }) {
    const [reason, setReason] = useState("");
    const approve = target.action === "approve";

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && !busy && onCancel();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [busy, onCancel]);

    const canConfirm = approve || reason.trim().length > 0;

    return (
        <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}
        >
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                <h3 className="text-lg font-bold">{approve ? "Approve request" : "Reject request"}</h3>
                <p className="mt-1 text-sm text-gray-500">
                    {target.row.customerName} · {target.row.programName} · PAN {target.row.pan}
                </p>

                {approve ? (
                    <p className="mt-4 text-sm text-gray-700">
                        The customer will be able to pay for this membership as soon as you approve.
                    </p>
                ) : (
                    <div className="mt-4">
                        <label className="mb-1 block text-xs font-medium text-gray-600">Reason (shown to the customer)</label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            maxLength={1000}
                            autoFocus
                            placeholder="Why is this request being rejected?"
                            className={inputCls}
                        />
                    </div>
                )}

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="rounded-xl border px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm(reason.trim())}
                        disabled={!canConfirm || busy}
                        className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                            approve ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                        }`}
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {approve ? "Approve" : "Reject"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function MembershipInvites() {
    useDocumentTitle("Welcome to Admin | Membership Requests");
    const dispatch = useDispatch();
    const { list, reviewingId } = useSelector((state) => state.membershipInvite);
    const { rows, loading, error, pagination } = list;
    const totalPages = pagination?.last_page || 1;
    const total = pagination?.total || 0;
    const from = pagination?.from || 0;
    const to = pagination?.to || 0;

    const [draft, setDraft] = useState(defaultFilters);
    const [applied, setApplied] = useState(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [reviewTarget, setReviewTarget] = useState(null); // { row, action }

    const load = () => dispatch(fetchInvites(buildParams(applied, currentPage)));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, currentPage, applied]);

    const handleDraft = (e) => setDraft((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleApply = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setApplied({ ...draft });
    };

    const handleReset = () => {
        const fresh = defaultFilters();
        setDraft(fresh);
        setCurrentPage(1);
        setApplied(fresh);
    };

    const handleConfirm = async (reason) => {
        const { row, action } = reviewTarget;
        try {
            await dispatch(reviewInviteThunk({ id: row.id, action, reason: action === "reject" ? reason : null })).unwrap();
            toastr.success(action === "approve" ? "Request approved" : "Request rejected");
            setReviewTarget(null);
            load(); // a reviewed row may no longer match the current status filter
        } catch (msg) {
            toastr.error(typeof msg === "string" ? msg : "Unable to update this request.");
        }
    };

    const entries = rows.map(normalizeInviteRow);

    return (
        <div className="w-full bg-white shadow-lg rounded-2xl p-6">
            <div className="mb-4">
                <h2 className="text-2xl font-bold">Membership Requests</h2>
                <p className="text-sm text-gray-500 mt-1">Customers asking to be considered for invite-only memberships.</p>
            </div>

            {/* Filters */}
            <form onSubmit={handleApply} className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select name="status" value={draft.status} onChange={handleDraft} className={inputCls}>
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">PAN</label>
                    <input
                        type="text"
                        name="keyword"
                        value={draft.keyword}
                        onChange={(e) => setDraft((prev) => ({ ...prev, keyword: e.target.value.toUpperCase() }))}
                        placeholder="Search by PAN"
                        maxLength={10}
                        className={inputCls}
                    />
                </div>
                <div className="flex items-end gap-2">
                    <button type="submit" className="flex-1 inline-flex items-center justify-center gap-1 py-2 px-3 bg-[#f4a406] text-white rounded-xl hover:bg-[#d89005] transition-colors text-sm">
                        <Search size={16} /> Search
                    </button>
                    <button type="button" onClick={handleReset} title="Reset filters" className="py-2 px-3 border rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">
                        <RotateCcw size={16} />
                    </button>
                </div>
            </form>

            {error && (
                <div className="mb-4 flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                    <span>{error}</span>
                    <button type="button" onClick={load} className="font-medium underline">
                        Retry
                    </button>
                </div>
            )}

            {/* Requests */}
            <div className="overflow-auto border rounded-xl">
                <table className="w-full table-fixed text-sm text-left">
                    <thead className="text-gray-700 bg-gray-100">
                        <tr>
                            <th className="p-3 w-[24%]">Customer &amp; date</th>
                            <th className="p-3 w-[14%]">Membership</th>
                            <th className="p-3 w-[18%]">PAN &amp; record</th>
                            <th className="p-3 w-[16%]">Social media</th>
                            <th className="p-3 w-[14%]">Status</th>
                            <th className="p-3 w-[14%] text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {loading ? (
                            <tr>
                                <td className="p-3" colSpan={6}>
                                    <ShimmerLoader />
                                    <ShimmerLoader />
                                    <ShimmerLoader />
                                </td>
                            </tr>
                        ) : entries.length > 0 ? (
                            entries.map((e) => {
                                const st = STATUS_STYLE[e.status] || STATUS_FALLBACK;
                                const socials = Object.entries(e.social).filter(([, v]) => v);
                                const busy = reviewingId === e.id;
                                return (
                                    <tr key={e.id} className="hover:bg-gray-50 align-top">
                                        <td className={`p-3 break-words ${st.edge}`}>
                                            <div className="font-medium">{e.customerName}</div>
                                            <div className="text-xs text-gray-500 break-all">{e.customerEmail}</div>
                                            <div className="text-xs text-gray-500">{e.customerMobile}</div>
                                            <div className="text-xs text-gray-700 mt-1">
                                                {e.createdAt ? dayjs(e.createdAt).format("DD MMM YYYY, hh:mm A") : "-"}
                                            </div>
                                        </td>
                                        <td className="p-3 break-words font-medium">{e.programName}</td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-1.5 font-medium tracking-wider">
                                                {e.pan}
                                                {e.panVerified && <BadgeCheck size={15} className="text-green-600" title="PAN verified" />}
                                            </div>
                                            <span
                                                className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    e.noCriminalRecord
                                                        ? "bg-green-50 text-green-700 border border-green-200"
                                                        : "bg-red-50 text-red-700 border border-red-200"
                                                }`}
                                            >
                                                {!e.noCriminalRecord && <ShieldAlert size={12} />}
                                                {e.noCriminalRecord ? "No criminal record" : "Has criminal record"}
                                            </span>
                                        </td>
                                        <td className="p-3 break-words text-xs">
                                            {socials.length > 0 ? (
                                                socials.map(([k, v]) => (
                                                    <div key={k}>
                                                        <span className="text-gray-500">{SOCIAL_LABELS[k] || k}: </span>
                                                        <span className="break-all">{v}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="p-3 break-words">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${st.badge}`}>{st.label}</span>
                                            {e.status !== STATUS_PENDING && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {e.reviewerName && <div>by {e.reviewerName}</div>}
                                                    {e.reviewedAt && <div>{dayjs(e.reviewedAt).format("DD MMM YYYY")}</div>}
                                                </div>
                                            )}
                                            {e.status === STATUS_REJECTED && e.rejectionReason && (
                                                <div className="text-xs text-red-600 mt-1">{e.rejectionReason}</div>
                                            )}
                                        </td>
                                        <td className="p-3 text-right">
                                            {e.status === STATUS_PENDING ? (
                                                <div className="flex justify-end gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setReviewTarget({ row: e, action: "approve" })}
                                                        disabled={busy}
                                                        title="Approve"
                                                        className="inline-flex items-center gap-1 rounded-xl bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-60"
                                                    >
                                                        <Check size={14} /> Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setReviewTarget({ row: e, action: "reject" })}
                                                        disabled={busy}
                                                        title="Reject"
                                                        className="inline-flex items-center gap-1 rounded-xl border border-red-300 px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
                                                    >
                                                        <X size={14} /> Reject
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">Reviewed</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td className="p-6 text-center text-gray-500" colSpan={6}>
                                    No membership requests found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-3">
                <p className="text-xs text-gray-500">{!loading && total > 0 ? `Showing ${from}–${to} of ${total}` : ""}</p>
                <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
            </div>

            {reviewTarget && (
                <ReviewModal
                    target={reviewTarget}
                    busy={reviewingId === reviewTarget.row.id}
                    onCancel={() => setReviewTarget(null)}
                    onConfirm={handleConfirm}
                />
            )}
        </div>
    );
}